import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import {
  getComplianceScaleColor,
  type RankingPanelProps,
  type SectionStatus,
  type SortDirection,
  useListPagination,
} from '@src/packages/dashboard/components/ReportSummary';

import { MOST_EXECUTED_COLOR, RANKING_PAGE_SIZE } from '../constants';
import { hasCompletionRate, type TemplateRankingMetric } from '../historySelectors';
import { sortTemplatesByCompliance, sortTemplatesByOrders } from '../selectors';
import type { TasksSummaryProps } from '../types';

type RankingSortProps = Pick<
  TasksSummaryProps,
  'executionSort' | 'complianceSort' | 'onExecutionSortChange' | 'onComplianceSortChange'
>;

/**
 * Template rankings: most executed and lowest compliance, each sortable and paginated.
 * Templates without a result cohort (only canceled orders) have no compliance to rank.
 */
export const useTemplateRankings = (
  templates: TemplateRankingMetric[],
  { executionSort, complianceSort, onExecutionSortChange, onComplianceSortChange }: RankingSortProps,
  status: SectionStatus,
): Array<RankingPanelProps & { key: string }> => {
  const intl = useIntl();
  const byExecution = useMemo(
    () => sortTemplatesByOrders(templates, executionSort),
    [executionSort, templates],
  );
  const byCompliance = useMemo(
    () => sortTemplatesByCompliance(templates.filter(hasCompletionRate), complianceSort),
    [complianceSort, templates],
  );
  const executionPages = useListPagination(byExecution, RANKING_PAGE_SIZE);
  const compliancePages = useListPagination(byCompliance, RANKING_PAGE_SIZE);
  const maxOrders = Math.max(...templates.map(template => template.orders), 1);

  const message = (id: string, values?: Record<string, number>) => intl.formatMessage({ id }, values);
  const rangeLabel = (pages: typeof executionPages) =>
    message('dashboards.tasks.summary.templates.showingRange', {
      from: pages.from,
      to: pages.to,
      total: pages.total,
    });
  const pagination = (pages: typeof executionPages, key: string) => ({
    page: pages.page,
    pageSize: pages.pageSize,
    total: pages.total,
    ariaLabel: message(`dashboards.tasks.summary.templates.${key}.navigation`),
    onChange: pages.setPage,
  });

  return [
    {
      key: 'most',
      title: message('dashboards.tasks.summary.templates.executionTitle'),
      description: message('dashboards.tasks.summary.templates.executionDetail'),
      sort: {
        value: executionSort,
        ariaLabel: message('dashboards.tasks.summary.templates.executionSort'),
        options: [
          { value: 'desc', label: message('dashboards.tasks.summary.templates.sort.most') },
          { value: 'asc', label: message('dashboards.tasks.summary.templates.sort.least') },
        ],
        onChange: (value: SortDirection) => {
          onExecutionSortChange(value);
          executionPages.reset();
        },
      },
      items: executionPages.items.map(template => ({
        id: template.templateId,
        name: template.templateName,
        percent: Math.max(0, Math.round((template.orders / maxOrders) * 100)),
        color: MOST_EXECUTED_COLOR,
        value: template.orders,
        detail: message('dashboards.tasks.summary.templates.orders', { count: template.orders }),
      })),
      pagination: pagination(executionPages, 'most'),
      rangeLabel: rangeLabel(executionPages),
      status,
    },
    {
      key: 'lowest',
      title: message('dashboards.tasks.summary.templates.complianceTitle'),
      description: message('dashboards.tasks.summary.templates.complianceDetail'),
      sort: {
        value: complianceSort,
        ariaLabel: message('dashboards.tasks.summary.templates.complianceSort'),
        options: [
          { value: 'asc', label: message('dashboards.tasks.summary.templates.sort.lowest') },
          { value: 'desc', label: message('dashboards.tasks.summary.templates.sort.highest') },
        ],
        onChange: (value: SortDirection) => {
          onComplianceSortChange(value);
          compliancePages.reset();
        },
      },
      items: compliancePages.items.map(template => ({
        id: template.templateId,
        name: template.templateName,
        percent: Math.max(0, Math.round(template.completionRate)),
        color: getComplianceScaleColor(template.completionRate),
        value: `${Math.round(template.completionRate)}%`,
        detail: message('dashboards.tasks.summary.templates.completedOfOrders', {
          completed: template.completed,
          total: template.resultCohort,
        }),
      })),
      pagination: pagination(compliancePages, 'lowest'),
      rangeLabel: rangeLabel(compliancePages),
      status,
    },
  ];
};
